import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Download, ClipboardCheck, Users, BookOpen, ArrowRight } from "lucide-react";

const STEPS = [
  { icon: ClipboardCheck, to: "/survey/opening", title: "Complete the entry survey", text: "Five minutes. It unlocks your dashboard and helps us shape the programme around you." },
  { icon: Users, to: "/my-team", title: "Meet your team", text: "See who you're working with, agree a team leader, and post your first note." },
  { icon: BookOpen, to: "/workbook", title: "Open Session 1", text: "Read the pre-work in the Sessions workbook so you arrive ready." },
];

export default function WelcomePopup() {
  const { user, setUser } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const open = !!user && user.role === "student" && !user.welcome_seen_at && !dismissed;

  const close = () => {
    setDismissed(true);
    setUser({ ...user, welcome_seen_at: new Date().toISOString() });
    api.post("/users/me/welcome-seen").catch(() => {});
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="rounded-none max-w-xl p-0 overflow-hidden border-2 border-[#0F1F2E]" data-testid="welcome-popup">
        <div className="bg-[#0F1F2E] px-7 py-6">
          <div className="flex gap-1 mb-4"><span className="w-4 h-4 bg-[#F4B847]" /><span className="w-4 h-4 bg-[#25AAAA]" /></div>
          <div className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#25AAAA]">Welcome to iTeams</div>
          <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tighter text-white mt-1 leading-tight">
            Hello, {user?.name?.split(" ")[0] || "there"}. Here&apos;s how to start.
          </h2>
        </div>
        <div className="px-7 py-6 space-y-5">
          <ol className="space-y-3" data-testid="welcome-steps">
            {STEPS.map((s, i) => (
              <li key={s.to}>
                <Link to={s.to} onClick={close} className="flex items-start gap-4 border border-[#E5E7EB] p-3 hover:border-[#25AAAA] hover:bg-[#E6F7F7] transition-colors group"
                      data-testid={`welcome-step-${i + 1}`}>
                  <span className="w-8 h-8 shrink-0 bg-[#0F1F2E] text-[#F4B847] font-display font-black flex items-center justify-center">{i + 1}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-semibold text-sm text-[#0F1F2E] flex items-center gap-2"><s.icon size={14} className="text-[#25AAAA]" /> {s.title}</span>
                    <span className="block text-xs text-[#525252] mt-0.5 leading-relaxed">{s.text}</span>
                  </span>
                  <ArrowRight size={14} className="text-[#525252] opacity-0 group-hover:opacity-100 mt-2 transition-opacity" />
                </Link>
              </li>
            ))}
          </ol>
          <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
            <a href="/guide/teams.pdf" target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-2 bg-[#0F1F2E] text-[#F4B847] px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#1a2f42] transition-colors"
               data-testid="welcome-guide-link">
              <Download size={14} /> Team Guide (PDF)
            </a>
            <button onClick={close} className="text-xs font-semibold uppercase tracking-wider text-[#525252] hover:text-[#0F1F2E] underline-offset-4 hover:underline"
                    data-testid="welcome-dismiss-btn">
              Got it, let me in
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
